import c from 'classnames';
import React, { useEffect, useState } from 'react';
import Info from '../components/Info';

export const TOGGLE_ACTIVE_CSS = 'right-0 border-green-500';

interface Props {
    name: string;
    label?: string;
    info?: string;
    checked?: boolean;
    onChange?: (checked: boolean) => void;
    onBeforeChange?: (checked: boolean) => void;
    style?: any; // ts bug can't use CSSProperties
    disabledColour?: 'GRAY' | 'YELLOW';
    checkedColour?: 'GREEN' | 'GRAY';
    text?: { checked: string; unchecked: string };
    disabled?: boolean;
    children?: React.ReactNode;
}

export default function Toggle({
    name,
    label,
    info,
    checked: initialState = false,
    onChange,
    style,
    disabledColour = 'GRAY',
    checkedColour = 'GREEN',
    text,
    disabled = false,
    children,
}: Props) {
    const [checked, setChecked] = useState(initialState);
    useEffect(() => setChecked(initialState), [initialState]);
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!onChange) {
            return;
        }
        event.stopPropagation();
        const isChecked = event.target.checked;
        setChecked(isChecked);
        onChange(isChecked);
    };

    return (
        <div className="flex items-center">
            <div
                className={c('relative inline-block mr-2 align-middle select-none transition duration-200 ease-in', {
                    'w-10': !text,
                    'w-16': !!text,
                    'text-left': checked,
                    'text-right': !checked,
                    'pointer-events-none': disabled,
                })}
                style={style}
            >
                <input
                    type="checkbox"
                    name={name}
                    id={`toggle${name}`}
                    disabled={disabled}
                    className={c(
                        'absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer',
                        {
                            [TOGGLE_ACTIVE_CSS]: checked,
                            'border-gray-300': !checked && disabledColour === 'GRAY',
                            'border-yellow-600': !checked && disabledColour === 'YELLOW',
                        }
                    )}
                    checked={checked}
                    onChange={handleChange}
                />
                <label
                    htmlFor={`toggle${name}`}
                    className={c('block overflow-hidden h-6 rounded-full cursor-pointer', {
                        'bg-green-500': checked && checkedColour === 'GREEN',
                        'bg-gray-300': (!checked && disabledColour === 'GRAY') || (checked && checkedColour === 'GRAY'),
                        'bg-yellow-600': !checked && disabledColour === 'YELLOW',
                    })}
                >
                    {text && (
                        <span className={'font-medium text-xs text-white uppercase inline-block mt-1 mx-2'}>
                            {checked && text.checked}
                            {!checked && text.unchecked}
                        </span>
                    )}
                </label>
            </div>
            {label && (
                <label htmlFor={`toggle${name}`} className="text-xs text-gray-700">
                    {label}
                    {info && <Info info={info} className="ml-1" />}
                </label>
            )}
            {children}
        </div>
    );
}
